import Decimal from "decimal.js";
import { FC, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CollapseGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Paragraph, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

import { MedicareAmount, MemberAccumulationsResponse } from "@/App/api/member";
import { dollarValueOrBlank, formatDollars, subtract } from "@/App/utils/utils";

interface MedicareOverviewProps {
  readonly hasPartB: boolean;
  readonly hasPartD: boolean;
  readonly medicareAmount: MedicareAmount;
  readonly memberAccumulations: MemberAccumulationsResponse;
  readonly isLoadingMemberAccumulations: boolean;
}

type Phase = "initialCoverage" | "deductible" | "catastrophic";
type Spend = "member" | null;

export const MedicareOverview: FC<MedicareOverviewProps> = ({ hasPartD, medicareAmount, memberAccumulations, isLoadingMemberAccumulations }) => {
  const [phase, setPhase] = useState<Phase>("deductible");
  const [amount, setAmount] = useState("");
  const [spend, setSpend] = useState<Spend>("member");
  const [nextPhase, setNextPhase] = useState<Phase>("initialCoverage");
  const [isExpanded, setIsExpanded] = useState(false);

  const { t: translate } = useTranslation();

  const phases = {
    initialCoverage: translate("BENEFITS.medicareOverview.phaseInitialCoverageText"),
    deductible: translate("BENEFITS.medicareOverview.phaseDeductibleText"),
    catastrophic: translate("BENEFITS.medicareOverview.phaseCatastrophicText"),
  };
  const spends = {
    member: translate("BENEFITS.medicareOverview.spendMemberText"),
  };

  const partBMaximumOutOfPocketMessage = useMemo(() => {
    if (memberAccumulations?.part_b_maximum_out_of_pocket) {
      return <>{translate("BENEFITS.medicareOverview.partBMaximumOutOfPocketNoDeductibleMessage")}</>;
    } else {
      return (
        <Trans
          i18nKey="BENEFITS.medicareOverview.partBMaximumOutOfPocketWithDeductibleMessage"
          values={{ maxDeductible: formatDollars(memberAccumulations?.part_b_deductible?.individual_max_amount) }}
        />
      );
    }
  }, [memberAccumulations]);

  useEffect(() => {
    const deductible = memberAccumulations?.deductible;
    const true_out_of_pocket = memberAccumulations?.true_out_of_pocket;

    if (
      deductible?.individual_max_amount
      && deductible?.individual_amount
      && new Decimal(deductible?.individual_amount).lessThan(new Decimal(deductible?.individual_max_amount))
    ) {
      setAmount(subtract(deductible?.individual_max_amount, deductible.individual_amount));
    } else if (
      true_out_of_pocket?.individual_amount
      && true_out_of_pocket?.individual_max_amount
      && new Decimal(true_out_of_pocket?.individual_amount).lessThan(new Decimal(true_out_of_pocket?.individual_max_amount))
    ) {
      setPhase("initialCoverage");
      setAmount(subtract(true_out_of_pocket?.individual_max_amount, true_out_of_pocket?.individual_amount));
      setSpend("member");
      setNextPhase("catastrophic");
    } else if (
      true_out_of_pocket?.individual_amount
      && true_out_of_pocket?.individual_max_amount
      && new Decimal(true_out_of_pocket?.individual_amount).greaterThanOrEqualTo(new Decimal(true_out_of_pocket?.individual_max_amount))
    ) {
      setPhase("catastrophic");
    } else {
      return;
    }
  }, [medicareAmount, memberAccumulations]);

  return (
    <Stack>
      <Subheading>{translate("BENEFITS.medicareOverview.overview")}</Subheading>
      {isLoadingMemberAccumulations && (
        <Shelf>
          <Shelf.Spacer />
          <Spinner />
          <Shelf.Spacer />
        </Shelf>
      )}
      {!isLoadingMemberAccumulations && !memberAccumulations && (
        <Paragraph>
          {translate("BENEFITS.medicareOverview.loadingErrorMessage")}
        </Paragraph>
      )}
      {memberAccumulations && (
        <>
          {!hasPartD && (
            <Stack>
              <Paragraph>{partBMaximumOutOfPocketMessage}</Paragraph>
              <Paragraph>
                <Trans
                  i18nKey="BENEFITS.medicareOverview.partBMaximumOutOfPocketParagraphMessage"
                  values={{ maxOutOfPocket: formatDollars(memberAccumulations?.part_b_maximum_out_of_pocket?.individual_max_amount) }}
                />
              </Paragraph>
            </Stack>
          )}
          {hasPartD && (
            <Stack>
              <Paragraph>
                <Trans
                  i18nKey="BENEFITS.medicareOverview.memberAccumulationsPartDMemberPhase"
                  values={{ phase: phases[phase] }}
                  components={{ Semibold: <Semibold /> }}
                />
              </Paragraph>
              {phase !== "catastrophic" && (
                <Paragraph testId="phase-information">
                  <Trans
                    i18nKey="BENEFITS.medicareOverview.memberAccumulationsPartDPhaseInformation"
                    values={{
                      amount,
                      spend: spends[spend],
                      phase: phases[phase],
                      nextPhase: phases[nextPhase] || "",
                    }}
                    components={{ Semibold: <Semibold /> }}
                  />
                </Paragraph>
              )}
              <CollapseGroup
                onExpandedIdsChange={() => setIsExpanded((prev) => !prev)}
                expandedIds={isExpanded ? ["overviewMoreInfo"] : []}
              >
                <CollapseGroup.Collapse id="overviewMoreInfo" title={translate("BENEFITS.medicareOverview.overviewMoreInfo")}>
                  <Stack>
                    <Body>{translate("BENEFITS.medicareOverview.overviewMoreInfoDescription")}</Body>
                    <Body>
                      <ul>
                        <li>
                          <Trans
                            i18nKey="BENEFITS.medicareOverview.overviewMoreInfoDeductiblePhaseDescription"
                            values={{ maxDeductible: dollarValueOrBlank(memberAccumulations?.deductible?.individual_max_amount) }}
                            components={{ Semibold: <Semibold /> }}
                          />
                        </li>
                        <li>
                          <Trans
                            i18nKey="BENEFITS.medicareOverview.overviewMoreInfoInitialCoveragePhaseDescription"
                            values={{ maxGrossCoveredDrugCost: dollarValueOrBlank(memberAccumulations?.gross_covered_drug_cost?.individual_max_amount) }}
                            components={{ Semibold: <Semibold /> }}
                          />
                        </li>
                        <li>{translate("BENEFITS.medicareOverview.overviewMoreInfoCatastrophicPhaseDescription")}</li>
                      </ul>
                    </Body>
                  </Stack>
                </CollapseGroup.Collapse>
              </CollapseGroup>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};
